import {
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpResponse,
} from '@angular/common/http';
import { inject, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { catchError, tap, throwError } from 'rxjs';

import { CommonService } from '@service/common/common.service';
import { AuthService } from '@service/auth/auth.service';
import {IS_FASTAPI_REQUEST} from '@app/http/fastapi-request.context';

export const rpcInterceptor: HttpInterceptorFn = (request, next) => {
  if (request.context.get(IS_FASTAPI_REQUEST)) {
    return next(request);
  }
  const injector = inject(Injector);
  const translate = inject(TranslateService);
  const router = inject(Router);
  const common = inject(CommonService);

  const currentLang = translate?.getCurrentLang?.() ?? 'en_GB';

  const base = request.clone({
    setHeaders: {
      'Frontend-Lang': currentLang,
    },
  });

  const shouldWrapAsJsonRpc =
    base.method === 'POST' &&
    !(base.body instanceof FormData);

  const finalRequest = shouldWrapAsJsonRpc
    ? base.clone({
      body: _buildJsonRpcBody(base.body, currentLang),
    })
    : base;

  return next(finalRequest).pipe(
    tap((event) => {
      if (!(event instanceof HttpResponse)) {
        return;
      }

      const body: any = event.body;

      if (body?.result?.error && !body?.result?.success) {
        const errorKey = _getRpcErrorKey(body);

        if (body?.result?.code === 404) {
          router.navigate(['catalog-list'], {
            replaceUrl: true,
            state: { bypassFormGuard: true },
          });
        } else {
          common.showErrorToast(errorKey);
        }

        return;
      }

      if (_isSessionExpired(body)) {
        const authService = injector.get(AuthService);
        queueMicrotask(() => authService.logout());
      }
    }),
    catchError((error: HttpErrorResponse | any) => {
      const msg = error?.error?.message;
      const code = error?.error?.code;

      if (msg && code !== 404) {
        common.showErrorToast(msg);
      }

      return throwError(() => error);
    })
  );
};

function _buildJsonRpcBody(originalBody: any, currentLang: string): any {
  return {
    jsonrpc: '2.0',
    method: 'call',
    params: {
      context: { lang: currentLang },
      id: null,
      data: originalBody,
    },
  };
}

function _isSessionExpired(body: any): boolean {
  return body?.error?.data?.name === 'odoo.http.SessionExpiredException';
}

function _getRpcErrorKey(body: any): string {
  return body?.result?.error ?? 'ERRORE_GENERICO';
}
